import ScrollReveal from '../components/marketing/ScrollReveal'

// ─── Design tokens ───
const t = {
  bg: '#FDFCFB',
  bgDark: '#252019',
  text: '#2E2318',
  textBody: '#6B5E52',
  textFaint: '#9E9189',
  accent: '#C4622A',
  accentBtn: '#D97645',
  border: '#E8DED4',
  ctaBg: '#2E2318',
  fontMain: '"Geist", system-ui, sans-serif',
  fontSerif: '"Instrument Serif", system-ui, serif',
} as const

// ═══════════════════════════════════════════════
// REUSABLE COMPONENTS
// ═══════════════════════════════════════════════

function CornerLogo({ color = '#F5F2EC', size = 22 }: { color?: string; size?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
      <svg width={size} height={size} viewBox="0 0 256 256" fill="none">
        <defs>
          <linearGradient id="hiwLogoGrad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
            <stop stopColor="#D46A3A"/>
            <stop offset="1" stopColor="#8B3A10"/>
          </linearGradient>
        </defs>
        <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#hiwLogoGrad)" strokeWidth="40" strokeLinecap="round" />
      </svg>
      <span style={{ fontSize: 16, fontWeight: 600, color, fontFamily: t.fontMain, letterSpacing: '-0.01em', lineHeight: '20px' }}>Corner</span>
    </div>
  )
}

function PrimaryButton({ children, href }: { children: string; href: string }) {
  return (
    <a href={href} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 8, padding: '12px 28px',
      backgroundColor: t.accentBtn, textDecoration: 'none',
    }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: '#FFFFFF', fontFamily: t.fontMain, lineHeight: '18px' }}>{children}</span>
    </a>
  )
}

function OutlineButton({ children, href }: { children: string; href: string }) {
  return (
    <a href={href} style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      borderRadius: 8, padding: '12px 28px',
      border: '1px solid #6B5E52', textDecoration: 'none',
    }}>
      <span style={{ fontSize: 15, fontWeight: 500, color: '#FDFCFB', fontFamily: t.fontMain, lineHeight: '18px' }}>{children}</span>
    </a>
  )
}

// ═══════════════════════════════════════════════
// NAVBAR
// ═══════════════════════════════════════════════

function Navbar() {
  return (
    <nav style={{
      width: '100%', height: 56, display: 'flex', alignItems: 'center',
      justifyContent: 'space-between', padding: '0 48px',
      position: 'sticky', top: 0, zIndex: 100,
      backgroundColor: '#F2EDE6', borderBottom: '1px solid #E4DDD4',
      backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
    }}>
      <a href="/" style={{ textDecoration: 'none' }}>
        <CornerLogo color={t.text} size={22} />
      </a>
      <div style={{ display: 'flex', gap: 32 }}>
        {['Features', 'How it works', 'Pricing', 'FAQ'].map(link => (
          <a key={link} href={
            link === 'Features' ? '/features' :
            link === 'How it works' ? '/how-it-works' :
            link === 'Pricing' ? '/pricing' :
            `/#${link.toLowerCase().replace(/\s+/g, '-')}`
          } style={{
            fontSize: 14,
            fontWeight: link === 'How it works' ? 600 : 400,
            color: link === 'How it works' ? '#D46A3A' : '#8B7D6B',
            textDecoration: 'none',
            fontFamily: t.fontMain, lineHeight: '18px',
          }}>{link}</a>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
        <a href="/auth/login" style={{ fontSize: 14, fontWeight: 400, color: '#8B7D6B', textDecoration: 'none', fontFamily: t.fontMain, lineHeight: '18px' }}>Sign in</a>
        <a href="/auth/register" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: 8, padding: '8px 18px',
          backgroundColor: '#D46A3A', textDecoration: 'none',
        }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF', fontFamily: t.fontMain, lineHeight: '20px' }}>Get started →</span>
        </a>
      </div>
    </nav>
  )
}

// ═══════════════════════════════════════════════
// HERO SECTION
// ═══════════════════════════════════════════════

function HeroSection() {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', paddingTop: 60, paddingBottom: 48, paddingLeft: 120, paddingRight: 120, gap: 24,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        borderRadius: 100, padding: '8px 20px',
        border: '1px solid #E8DED4',
      }}>
        <span style={{ fontSize: 14, fontWeight: 500, letterSpacing: '0.02em', color: t.textBody, fontFamily: t.fontMain, lineHeight: '18px' }}>How it works</span>
      </div>

      <h1 style={{
        fontSize: 56, fontWeight: 400, lineHeight: '64px',
        textAlign: 'center', whiteSpace: 'pre-line',
        color: t.text, fontFamily: t.fontSerif, margin: 0, maxWidth: 700,
      }}>
        {'Documents come in. Get worked on.\nGo out.'}
      </h1>

      <p style={{
        fontSize: 18, fontWeight: 300, lineHeight: '28px',
        textAlign: 'center', color: t.textBody, fontFamily: t.fontMain,
        margin: 0, maxWidth: 520,
      }}>
        Corner is the single place where your files arrive, get read by AI, acted on, and sent — without switching tools.
      </p>
    </section>
  )
}

// ═══════════════════════════════════════════════
// STEP 05 — COLLABORATE
// ═══════════════════════════════════════════════

function CollaborateStep() {
  return (
    <section style={{
      display: 'flex', justifyContent: 'center', width: '100%',
      backgroundColor: '#F5F2ED', padding: '80px 120px',
    }}>
      <div style={{
        maxWidth: 1100, width: '100%',
        display: 'flex', alignItems: 'center', gap: 64,
      }}>
        {/* Text (left) */}
        <div style={{ width: 360, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 42, fontWeight: 300, color: '#D46A3A', fontFamily: '"Instrument Serif", Georgia, serif', lineHeight: '42px' }}>05</span>
            <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: '0.12em', textTransform: 'uppercase' as const, color: '#8B7D6B', fontFamily: t.fontMain }}>Collaborate</span>
          </div>
          <h2 style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: '38px', color: t.text, fontFamily: t.fontMain, margin: '0 0 16px' }}>
            Review together, decide faster
          </h2>
          <p style={{ fontSize: 15, lineHeight: '24px', color: t.textBody, fontFamily: t.fontMain, margin: 0 }}>
            Share any document with your team. Drop comments on specific clauses, see who's viewing in real time, and discuss findings in a shared AI chat — no switching to email or Slack.
          </p>
        </div>

        {/* Screenshots (right) — stacked with offset share modal */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
          <img
            src="/Howitworkscreen/17cCollaborationComment Pins@2x.png"
            alt="Comment pins on document"
            style={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)' }}
            loading="lazy"
          />
          <img
            src="/Howitworkscreen/17bCollaborationShareInvite@2x.png"
            alt="Share and invite collaborators"
            style={{ width: '55%', alignSelf: 'flex-end', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)' }}
            loading="lazy"
          />
        </div>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// IMAGE SECTIONS (exported PNGs)
// ═══════════════════════════════════════════════

function ImageSection({ src, alt, bgColor }: { src: string; alt: string; bgColor?: string }) {
  return (
    <section style={{
      display: 'flex', justifyContent: 'center', width: '100%', alignSelf: 'stretch',
      backgroundColor: bgColor || t.bg,
    }}>
      <img
        src={src}
        alt={alt}
        style={{ width: '100%', maxWidth: 1440, display: 'block' }}
        loading="lazy"
      />
    </section>
  )
}

// ═══════════════════════════════════════════════
// CLOSING CTA SECTION
// ═══════════════════════════════════════════════

function ClosingCTA() {
  return (
    <section style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      width: '100%', alignSelf: 'stretch', padding: '80px 120px', gap: 24,
      backgroundColor: t.ctaBg,
    }}>
      <h2 style={{
        fontSize: 40, fontWeight: 400, lineHeight: '48px',
        textAlign: 'center', color: '#FDFCFB',
        fontFamily: t.fontSerif, margin: 0,
      }}>
        Ready to put your documents to work?
      </h2>

      <p style={{
        fontSize: 16, fontWeight: 300, lineHeight: '26px',
        textAlign: 'center', color: '#B8AFA5', fontFamily: t.fontMain,
        margin: 0, maxWidth: 440,
      }}>
        No account required to start. Upload a file and see what Corner can do in seconds.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, paddingTop: 8 }}>
        <PrimaryButton href="/auth/register">Get started free</PrimaryButton>
        <OutlineButton href="/pricing">View pricing</OutlineButton>
      </div>
    </section>
  )
}

// ═══════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════

export default function HowItWorksPage() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      backgroundColor: t.bg, fontFamily: t.fontMain, minHeight: '100vh',
    }}>
      <Navbar />
      <ScrollReveal style={{ width: '100%' }}><HeroSection /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/Howitworkscreen/Step1Bringitin@2x.png" alt="Step 1 — Bring it in: Import from anywhere or just drag and drop" bgColor="#F5F2ED" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/Howitworkscreen/Step2Understand@2x.png" alt="Step 2 — Understand: Corner reads, flags, and extracts instantly" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/Howitworkscreen/Step3Act@2x.png" alt="Step 3 — Act: Sign, send, and transform without leaving" bgColor="#F5F2ED" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}>
        <ImageSection src="/Howitworkscreen/Step4Automate@2x.png" alt="Step 4 — Automate: Chain it all into a workflow" />
      </ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><CollaborateStep /></ScrollReveal>
      <ScrollReveal style={{ width: '100%' }}><ClosingCTA /></ScrollReveal>
    </div>
  )
}
